<template>
  <div
    :class="overlay ? 'item-holder-scrim' : 'item-holder-inline'"
    @click.self="overlay && close()"
  >
    <div
      class="item-holder"
      :class="{ 'item-holder--overlay': overlay, 'item-holder--disabled': disabled }"
      :role="overlay ? 'dialog' : null"
      :aria-modal="overlay ? 'true' : null"
    >
      <template v-if="overlay">
        <span class="holder-corner holder-corner--tl" aria-hidden="true"></span>
        <span class="holder-corner holder-corner--tr" aria-hidden="true"></span>
        <span class="holder-corner holder-corner--bl" aria-hidden="true"></span>
        <span class="holder-corner holder-corner--br" aria-hidden="true"></span>
      </template>

      <!-- Header with slot and close button -->
      <div class="header" v-if="showHeader">
        <div class="header-label">
          <slot name="header"></slot>
        </div>
        <button
          class="close-btn"
          type="button"
          :title="$t('player.common.close')"
          :aria-label="$t('player.common.close')"
          @click="close"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>

      <!-- Filter and sort toolbar -->
      <div class="toolbar" v-if="controls">
        <div class="filters" role="group" :aria-label="$t('player.inventory.filterBy')">
          <button
            type="button"
            class="chip"
            :class="{ 'is-active': activeType === ALL_TYPES }"
            @click="activeType = ALL_TYPES"
          >
            {{ $t('player.inventory.all') }}
            <span class="chip-count">{{ (items || []).length }}</span>
          </button>
          <button
            v-for="type in availableTypes"
            :key="type"
            type="button"
            class="chip"
            :class="[`chip--${type}`, { 'is-active': activeType === type }]"
            @click="activeType = type"
          >
            {{ type }}
            <span class="chip-count">{{ countOfType(type) }}</span>
          </button>
        </div>

        <div class="sorts" role="group" :aria-label="$t('player.inventory.sortBy')">
          <span class="sorts-label">{{ $t('player.inventory.sortBy') }}</span>
          <button
            v-for="key in SORT_KEYS"
            :key="key"
            type="button"
            class="sort-btn"
            :class="{ 'is-active': sortKey === key }"
            :aria-pressed="sortKey === key"
            @click="applySort(key)"
          >
            {{ $t(`player.inventory.sort.${key}`) }}
            <span class="sort-dir" v-if="sortKey === key" aria-hidden="true">
              {{ sortDir === 'asc' ? '▲' : '▼' }}
            </span>
          </button>
        </div>
      </div>

      <!-- Grid with pagination -->
      <RPGGridWithScroller
        :key="gridKey"
        :row-count="rowCount"
        :col-count="colCount"
        :cell-size="cellSize"
        :items="visibleItems"
        :initial-page="currentPage + 1"
        class="item-grid"
      >
        <template #default="{ item }">
          <ItemCell
            class="item-cell"
            v-if="item"
            :itemData="item"
            @click="handleItemClick(item)"
          />
        </template>
      </RPGGridWithScroller>

      <p class="empty-note" v-if="controls && !visibleItems.length">
        {{ (items || []).length ? $t('player.inventory.noMatch') : $t('player.inventory.empty') }}
      </p>
    </div>
  </div>
</template>

<script>
import ItemCell from "@/components/Item/ItemCell.vue";
import RPGGridWithScroller from "@/components/RPGGrid/RPGGridWithScroller.vue";

const ALL_TYPES = "__all__";
const SORT_KEYS = ["name", "type", "charges"];

export default {
  name: "ItemHolder",
  components: {
    RPGGridWithScroller,
    ItemCell,
  },
  props: {
    showHeader: {
      type: Boolean,
      default: true, // Show the UI by default
    },
    items: {
      type: Array,
      default: () => [], // List of items to display
    },
    overlay: {
      type: Boolean,
      default: true, // Position as a centered fixed modal; false lets a parent lay it out
    },
    disabled: {
      type: Boolean,
      default: false, // Block interaction with the cells
    },
    rowCount: {
      type: Number,
      default: 3, // Default number of rows
    },
    colCount: {
      type: Number,
      default: 5, // Default number of columns
    },
    cellSize: {
      type: Number,
      default: 6, // Default size of each cell in rem
    },
    controls: {
      type: Boolean,
      default: false, // Filter/sort toolbar; a bargain or chest shelf does not want one
    },
  },
  data() {
    return {
      currentPage: 0, // Keep for compatibility with initial-page prop
      ALL_TYPES,
      SORT_KEYS,
      activeType: ALL_TYPES,
      sortKey: "type",
      sortDir: "asc",
    };
  },
  computed: {
    availableTypes() {
      const types = new Set();
      (this.items || []).forEach((entry) => {
        const type = entry?.item?.type;
        if (type) types.add(type);
      });
      return [...types].sort();
    },

    visibleItems() {
      const filtered =
        this.activeType === ALL_TYPES
          ? [...(this.items || [])]
          : (this.items || []).filter((entry) => entry?.item?.type === this.activeType);

      const direction = this.sortDir === "asc" ? 1 : -1;
      return filtered.sort((a, b) => this.compare(a, b) * direction);
    },

    /*
     * The scroller owns its page number internally, so a filter that shrinks the
     * list below the current page would leave the player staring at empty slots.
     * Remounting on any filter or sort change puts them back on page one.
     */
    gridKey() {
      return `${this.activeType}|${this.sortKey}|${this.sortDir}`;
    },
  },
  mounted() {
    if (this.overlay) window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
    countOfType(type) {
      return (this.items || []).filter((entry) => entry?.item?.type === type).length;
    },
    applySort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    compare(a, b) {
      const nameA = a?.item?.name || "";
      const nameB = b?.item?.name || "";
      if (this.sortKey === "charges") {
        // Unlimited-use items carry no charge count; they sort after the counted ones.
        const chargesA = a?.charges_left ?? -1;
        const chargesB = b?.charges_left ?? -1;
        if (chargesA !== chargesB) return chargesA - chargesB;
        return nameA.localeCompare(nameB);
      }
      if (this.sortKey === "type") {
        const typeA = a?.item?.type || "";
        const typeB = b?.item?.type || "";
        if (typeA !== typeB) return typeA.localeCompare(typeB);
        return nameA.localeCompare(nameB);
      }
      return nameA.localeCompare(nameB);
    },
    handleItemClick(item) {
      if (this.disabled) return;
      this.$emit("item-clicked", item);
    },
    close() {
      this.$emit("close"); // Emit close event to parent
    },
  },
};
</script>

<style scoped>
/* Embedded, the wrapper must not exist as far as the parent's layout is
   concerned - a bargain column positions the panel itself. */
.item-holder-inline {
  display: contents;
}

.item-holder-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  z-index: 9000;
  align-items: center;
  justify-content: center;
  /* A 40-slot grid is wider than a small laptop; scroll rather than clip. */
  overflow: auto;
  padding: 1.5rem 1rem;
  background: radial-gradient(ellipse at center, rgba(3, 11, 16, 0.72) 0%, rgba(2, 6, 9, 0.92) 100%);
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
}

.item-holder {
  --hud-cyan: #3ee9f0;
  --hud-cyan-dim: rgba(62, 233, 240, 0.45);
  --hud-line: rgba(0, 255, 255, 0.18);
  --hud-slab: rgba(11, 18, 24, 0.72);
  --hud-text: #d3e0e6;
  --hud-text-dim: #7f929c;
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: auto;
  color: var(--hud-text);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}

/* Only the standalone modal carries the frame; embedded in a bargain or a
   chest the parent panel already draws one. */
.item-holder--overlay {
  padding: 1.1rem 1.35rem 1.35rem;
  background: linear-gradient(160deg, rgba(12, 19, 25, 0.96) 0%, rgba(6, 10, 14, 0.985) 100%);
  border: 1px solid var(--hud-line);
  border-radius: 0.375rem;
  box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.72),
      inset 0 1px 0 rgba(62, 233, 240, 0.06);
}

.item-holder--overlay::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-image:
      linear-gradient(rgba(62, 233, 240, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(62, 233, 240, 0.035) 1px, transparent 1px);
  background-size: 2.25rem 2.25rem;
  mask-image: radial-gradient(ellipse at 50% 0%, #000 0%, transparent 78%);
  pointer-events: none;
}

.item-holder--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.holder-corner {
  position: absolute;
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid var(--hud-cyan-dim);
  pointer-events: none;
}

.holder-corner--tl { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.holder-corner--tr { top: -1px; right: -1px; border-width: 1px 1px 0 0; }
.holder-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 1px 1px; }
.holder-corner--br { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

.header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--hud-line);
}

.header-label {
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #f2c489;
  text-shadow: 0 1px 2px rgba(0, 10, 14, 0.95);
}

.close-btn {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--hud-text-dim);
  font-size: 0.7rem;
  line-height: 1;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.close-btn:hover {
  color: var(--hud-cyan);
  background: rgba(62, 233, 240, 0.08);
  border-color: var(--hud-cyan-dim);
}

.close-btn:focus-visible {
  outline: 1px solid var(--hud-cyan);
  outline-offset: 2px;
}

.toolbar {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
}

.filters,
.sorts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.chip {
  --tone: var(--hud-text-dim);

  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  padding: 0.2rem 0.45rem;
  color: var(--hud-text-dim);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.5rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  background: rgba(6, 10, 14, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 0.15rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

/* The chip carries the same hue the tile's edge-bar uses for that type. */
.chip--weapon { --tone: #e88a84; }
.chip--armor { --tone: #7fd6a2; }
.chip--artifact { --tone: #b79ae8; }
.chip--amulet { --tone: #f2c489; }
.chip--material { --tone: #9dc98a; }
.chip--quest { --tone: #8ab4e8; }
.chip--misc { --tone: #9aacb5; }
.chip--food { --tone: #e8a97f; }
.chip--rune { --tone: #3ee9f0; }

.chip:hover {
  color: var(--tone);
  border-color: color-mix(in srgb, var(--tone) 40%, transparent);
}

.chip.is-active {
  color: var(--tone);
  background: color-mix(in srgb, var(--tone) 10%, transparent);
  border-color: color-mix(in srgb, var(--tone) 55%, transparent);
}

.chip-count {
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--hud-text);
}

.sorts-label {
  margin-right: 0.15rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.5rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.2rem 0.4rem;
  color: var(--hud-text-dim);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.5rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  background: rgba(6, 10, 14, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 0.15rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.sort-btn:hover {
  color: var(--hud-cyan);
  border-color: rgba(62, 233, 240, 0.35);
}

.sort-btn.is-active {
  color: var(--hud-cyan);
  background: rgba(62, 233, 240, 0.08);
  border-color: var(--hud-cyan-dim);
}

.sort-dir {
  font-size: 0.45rem;
}

.chip:focus-visible,
.sort-btn:focus-visible {
  outline: 1px solid var(--hud-cyan);
  outline-offset: 2px;
}

.empty-note {
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.52rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-align: center;
  color: var(--hud-text-dim);
}

.item-grid {
  position: relative;
  z-index: 1;
  margin: 0 auto;
}

/* The shared RPGCell wears a stone border-image for the GM tools; inside an
   item grid it becomes an empty HUD socket. */
.item-grid :deep(.rpg-cell) {
  padding: 4%;
  background: rgba(6, 10, 14, 0.55);
  border: 1px dashed rgba(62, 233, 240, 0.14);
  border-image: none;
  border-radius: 0.2rem;
}

/* Two classes deep so it beats ItemCell's own fixed size, which is the floor
   for callers that place a tile outside a grid socket. */
.item-grid :deep(.item-cell) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
</style>
